/* global axios */
// DahReply
import { useAlert } from 'dashboard/composables';
import accountAPI from '../../api/account';

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

// The known plan catalog (confirmed by backend) — Starter and Basic are both
// non-gated tiers, so they collapse to the same 'basic' bucket every
// consumer already treats as "not pro/enterprise".
const PLAN_NAME_TIERS = {
  'starter plan': 'basic',
  'basic plan': 'basic',
  'pro plan': 'pro',
  'enterprise plan': 'enterprise',
};

// Prefer the entitlement response's own `plan_name` — it's the API's source
// of truth, and any *recognized* plan name is authoritative on its own: we
// deliberately don't cross-check it against `features.inboxes`, since a
// Starter/Basic account that happens to carry the same inbox count as a Pro
// plan would otherwise get silently promoted. A recognized-but-non-matching
// name (shouldn't happen given the catalog above, but fails closed) also
// defaults to 'basic' rather than guessing.
//
// The inbox-count heuristic is only used when plan_name is missing
// entirely — legacy responses and the Cypress fixtures never carry it.
const derivePlanTier = (planName, inboxLimit) => {
  if (!planName) {
    if (inboxLimit === '-1') return 'enterprise';
    if (inboxLimit === '3') return 'pro';
    return 'basic';
  }
  return PLAN_NAME_TIERS[planName.trim().toLowerCase()] || 'basic';
};

export const state = {
  account: null,
  status: null,
  url: null,
  knowledgeLimit: null,
  agentLimit: null,
  inboxLimit: null,
  billingUrl: null,
  studioUrl: null,
  credits: null,
  words: null,
  topUpUrl: null,
  creditUsed: null,
  topupHistoryArray: [],
  topupCredits: null,
  topupCreditsUsed: null,
  appointmentBranchesLimit: null,
  tier: null,
  planName: null,
  planProductId: null,
  currentPeriodEnd: null,
  isTrial: false,
  daysRemaining: null,
  isFetched: false,
  fetchedAccountId: null, // Track which account was fetched
  hasSubscribedBefore: null,
  module: {},
  moduleId: null,
  moduleName: null,
  creditThresholds: [],
  creditThresholdsUiFlags: {
    isFetching: false,
    isUpdating: false,
  },
  notificationEmails: [],
  notificationEmailsUiFlags: {
    isFetching: false,
    isUpdating: false,
  },
};

export const getters = {
  getKnowledgeLimit(state) {
    return state.knowledgeLimit;
  },
  getAgentLimit(state) {
    return state.agentLimit;
  },
  getInboxLimit(state) {
    return state.inboxLimit;
  },
  getSubscriptionStatus(state) {
    return state.status;
  },
  getBillingUrl(state) {
    return state.billingUrl;
  },
  getStudioUrl(state) {
    return state.studioUrl;
  },
  getCredits(state) {
    return state.credits;
  },
  getWordsLimit(state) {
    return state.words;
  },
  getTopUpUrl(state) {
    return state.topUpUrl;
  },
  getCreditUsed(state) {
    return state.creditUsed;
  },
  getTopupHistoryArray(state) {
    return state.topupHistoryArray;
  },
  getTopupCredits(state) {
    return state.topupCredits;
  },
  getTopupCreditsUsed(state) {
    return state.topupCreditsUsed;
  },
  getAppointmentBranchesLimit(state) {
    return state.appointmentBranchesLimit;
  },
  getSubscriptionTier(state) {
    return state.tier;
  },
  getPlanName(state) {
    return state.planName;
  },
  getPlanProductId(state) {
    return state.planProductId;
  },
  getCurrentPeriodEnd(state) {
    return state.currentPeriodEnd;
  },
  isTrial(state) {
    return state.isTrial;
  },
  getDaysRemaining(state) {
    return state.daysRemaining;
  },
  isSubscriptionFetched(state) {
    return state.isFetched;
  },
  getFetchedAccountId(state) {
    return state.fetchedAccountId;
  },
  getHasSubscribedBefore(state) {
    return state.hasSubscribedBefore;
  },
  getModule(state) {
    return state.module;
  },
  getModuleId(state) {
    return state.moduleId;
  },
  getModuleName(state) {
    return state.moduleName;
  },
  getCreditThresholds(state) {
    return state.creditThresholds;
  },
  getCreditThresholdsUiFlags(state) {
    return state.creditThresholdsUiFlags;
  },
  getNotificationEmails(state) {
    return state.notificationEmails;
  },
  getNotificationEmailsUiFlags(state) {
    return state.notificationEmailsUiFlags;
  },
};

export const mutations = {
  setKnowledgeLimit(state, knowledgeLimit) {
    state.knowledgeLimit = knowledgeLimit;
  },
  setAgentLimit(state, agentLimit) {
    state.agentLimit = agentLimit;
  },
  setInboxLimit(state, inboxLimit) {
    state.inboxLimit = inboxLimit;
  },
  setSubscriptionStatus(state, status) {
    state.status = status;
  },
  setBillingUrl(state, status) {
    state.billingUrl = status;
  },
  setStudioUrl(state, url) {
    state.studioUrl = url;
  },
  setCreditsLimit(state, credits) {
    state.credits = credits;
  },
  setWordsLimit(state, limit) {
    state.words = limit;
  },
  setTopUpUrl(state, url) {
    state.topUpUrl = url;
  },
  setCreditUsed(state, used) {
    state.creditUsed = used;
  },
  setTopupHistoryArray(state, history) {
    state.topupHistoryArray = history;
  },
  setTopupCredits(state, credits) {
    state.topupCredits = credits;
  },
  setTopupCreditsUsed(state, used) {
    state.topupCreditsUsed = used;
  },
  setAppointmentBranchesLimit(state, limit) {
    state.appointmentBranchesLimit = limit;
  },
  setSubscriptionTier(state, tier) {
    state.tier = tier;
  },
  setModule(state, module) {
    state.module = module;
  },
  setModuleId(state, moduleId) {
    state.moduleId = moduleId;
  },
  setModuleName(state, moduleName) {
    state.moduleName = moduleName;
  },
  setIsFetched(state, isFetched) {
    state.isFetched = isFetched;
  },
  setFetchedAccountId(state, accountId) {
    state.fetchedAccountId = accountId;
  },
  setHasSubscribedBefore(state, hasSubscribedBefore) {
    state.hasSubscribedBefore = hasSubscribedBefore;
  },
  setCreditThresholds(state, thresholds) {
    state.creditThresholds = thresholds;
  },
  setCreditThresholdsUiFlag(state, flag) {
    state.creditThresholdsUiFlags = { ...state.creditThresholdsUiFlags, ...flag };
  },
  setPlanName(state, planName) {
    state.planName = planName;
  },
  setPlanProductId(state, planProductId) {
    state.planProductId = planProductId;
  },
  setCurrentPeriodEnd(state, currentPeriodEnd) {
    state.currentPeriodEnd = currentPeriodEnd;
  },
  setIsTrial(state, isTrial) {
    state.isTrial = isTrial;
  },
  setDaysRemaining(state, daysRemaining) {
    state.daysRemaining = daysRemaining;
  },
  setNotificationEmails(state, emails) {
    state.notificationEmails = emails;
  },
  setNotificationEmailsUiFlag(state, flag) {
    state.notificationEmailsUiFlags = { ...state.notificationEmailsUiFlags, ...flag };
  },
  resetSubscription(state) {
    state.account = null;
    state.status = null;
    state.url = null;
    state.knowledgeLimit = null;
    state.agentLimit = null;
    state.inboxLimit = null;
    state.billingUrl = null;
    state.studioUrl = null;
    state.credits = null;
    state.words = null;
    state.topUpUrl = null;
    state.creditUsed = null;
    state.topupHistoryArray = [];
    state.topupCredits = null;
    state.topupCreditsUsed = null;
    state.appointmentBranchesLimit = null;
    state.tier = null;
    state.planName = null;
    state.planProductId = null;
    state.currentPeriodEnd = null;
    state.isTrial = false;
    state.daysRemaining = null;
    state.isFetched = false;
    state.fetchedAccountId = null;
    state.module = {};
    state.moduleId = null;
    state.moduleName = null;
    state.creditThresholds = [];
    state.creditThresholdsUiFlags = {
      isFetching: false,
      isUpdating: false,
    };
    state.notificationEmails = [];
    state.notificationEmailsUiFlags = {
      isFetching: false,
      isUpdating: false,
    };
  },
};

export const actions = {
  async fetchSubscription(
    { rootGetters, commit, state },
    { id, force = false } = {}
  ) {
    const accountId = Number(id) || rootGetters.getCurrentAccountId;
    if (!accountId) {
      console.error('No account ID provided.');
      useAlert('Account ID is missing. Please try again later.');
      return;
    }
    if (state.isFetched && state.fetchedAccountId === accountId && !force) return;
    try {
      const { data: response } = await axios.get(
        knowledgeProxy(accountId, `/account/${accountId}/entitlement`)
      );
      const features = response.features || {};
      // Feature values are strings and occasionally carry stray whitespace
      // (e.g. " 1") — trim, but keep as strings rather than parsing to
      // Number: several existing consumers do strict `=== '-1'` string
      // comparisons against these getters (branches/organization limits),
      // and converting to a numeric type would silently break those.
      const trimFeature = value =>
        value === undefined || value === null ? null : String(value).trim();

      const inboxLimit = trimFeature(features.inboxes);

      commit('setSubscriptionStatus', response.status);
      commit('setKnowledgeLimit', trimFeature(features.datasets));
      commit('setAgentLimit', trimFeature(features.agents));
      commit('setInboxLimit', inboxLimit);
      commit('setCreditsLimit', trimFeature(features.credits));
      commit('setWordsLimit', trimFeature(features.knowledge_words));
      commit('setCreditUsed', trimFeature(features.credits_used));
      commit('setTopupCredits', trimFeature(features.topup_credits));
      commit('setTopupCreditsUsed', trimFeature(features.topup_credits_used));
      commit(
        'setAppointmentBranchesLimit',
        trimFeature(features.appointment_branches)
      );
      const tier = derivePlanTier(response.plan_name, inboxLimit);
      commit('setSubscriptionTier', tier);
      commit('setPlanName', response.plan_name);
      commit('setPlanProductId', response.plan_product_id);
      commit('setCurrentPeriodEnd', response.current_period_end);
      commit('setIsTrial', response.is_trial);
      commit('setDaysRemaining', response.days_remaining);
      commit('setIsFetched', true);
      commit('setFetchedAccountId', accountId);
      // Best-effort: only admins are authorized (agents get a 403, ignored
      // below), and this only drives cosmetic widget branding, not billing
      // enforcement, so a failed sync isn't worth surfacing to the user.
      accountAPI
        .syncPlanFeatures(tier === 'pro' || tier === 'enterprise')
        .catch(() => {});
    } catch (err) {
      // A 404 means the account has never had a subscription (e.g. a brand new
      // signup) — that's an expected state, not a failure, so treat it as
      // "no active subscription" instead of alerting and throwing.
      if (err?.response?.status === 404) {
        commit('resetSubscription');
        commit('setIsFetched', true);
        commit('setFetchedAccountId', accountId);
        accountAPI.syncPlanFeatures(false).catch(() => {});
        return;
      }
      console.error(err);
      useAlert('Error fetching subscription plan of user, please check your connection or try again later.');
      throw err;
    }
  },
  async fetchSubscriptionHistory({ rootGetters, commit }, { id } = {}) {
    const accountId = id || rootGetters.getCurrentAccountId;
    if (!accountId) {
      console.error('No account ID provided.');
      useAlert('Account ID is missing. Please try again later.');
      return undefined;
    }
    try {
      const { data: response } = await axios.get(
        knowledgeProxy(accountId, `/account/${accountId}/subscription_history`)
      );
      commit('setHasSubscribedBefore', response.has_subscribed_before);
      return response.has_subscribed_before;
    } catch (err) {
      // A 404 means the account has no subscription history (e.g. a brand
      // new signup) — that's an expected state meaning "never subscribed",
      // not a failure.
      if (err?.response?.status === 404) {
        commit('setHasSubscribedBefore', false);
        return false;
      }
      console.error(err);
      throw err;
    }
  },
  async fetchStripeSession({ rootGetters, commit }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data: response } = await axios.post(
        knowledgeProxy(accountId, '/stripe/create_portal_session'),
        { account_id: accountId }
      );
      if (!response.url) {
        useAlert('It seems you have not subscribed to a plan yet. Please choose a plan to proceed.');
        window.location = `/app/accounts/${accountId}/pricing_table`;
        return;
      }
      commit('setBillingUrl', response.url.replace('https://', ''));
    } catch (err) {
      console.error(err);
      throw err;
    }
  },
  // Returns the absolute Stripe URL (Checkout or billing-portal plan-change
  // confirm screen — the BE picks) for the chosen plan. Deliberately does not
  // go through setBillingUrl, which strips the https:// prefix.
  // `interval` is 'monthly' or 'annual'.
  async fetchPlanSession({ rootGetters }, { plan, interval, returnUrl }) {
    const accountId = rootGetters.getCurrentAccountId;
    const { data } = await axios.post(
      knowledgeProxy(accountId, '/stripe/create_plan_session'),
      { account_id: accountId, plan, interval, return_url: returnUrl }
    );
    return data.url;
  },
  // Per-brand plan catalog (name, price for the interval, currency, features).
  // A plan/interval the brand does not sell is absent from the list. Prices
  // are before tax. `current` is the account's live subscription as
  // { plan, interval, status } (plan/interval null on a legacy price), or null
  // when there is none; it is the same on both intervals.
  async fetchPlans({ rootGetters }, { id, interval = 'monthly' } = {}) {
    const accountId = id || rootGetters.getCurrentAccountId;
    const { data } = await axios.get(
      knowledgeProxy(accountId, '/stripe/plans'),
      { params: { account_id: accountId, interval } }
    );
    return { plans: data.plans || [], current: data.current || null };
  },
  async fetchStudioSession({ rootGetters, commit }) {
    const accountId = rootGetters.getCurrentAccountId;
    const payload = { account_id: accountId };

    try {
      const { data: response } = await axios.post(
        knowledgeProxy(accountId, '/studio/create_session'),
        payload
      );
      commit('setStudioUrl', response.url.replace('https://', ''));
    } catch (err) {
      console.error(err);
      throw err;
    }
  },
  async topupCheckoutSession({ rootGetters, commit }, creditAmount) {
    const accountId = rootGetters.getCurrentAccountId;
    const payload = {
      // prettier-ignore
      "account_id": accountId,
      // prettier-ignore
      "credit_amount": creditAmount,
    };

    try {
      const { data: response } = await axios.post(
        knowledgeProxy(accountId, '/stripe/topup_checkout_session'),
        payload
      );
      commit('setTopUpUrl', response.url);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },
  async fetchTopupHistory({ commit, rootGetters }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data: response } = await axios.get(
        knowledgeProxy(accountId, `/account/${accountId}/top_up_history`)
      );
      commit('setTopupHistoryArray', response);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  // `accountIdOverride` is for route guards: during a cold-load
  // beforeEnter the synced route state still points at the previous
  // (empty) route, so getCurrentAccountId is null there.
  async fetchModule({ rootGetters, commit }, accountIdOverride) {
    const accountId = accountIdOverride || rootGetters.getCurrentAccountId;

    try {
      const { data: response } = await axios.get(
        knowledgeProxy(accountId, `/module-subscription/${accountId}/modules?active_only=true`)
      );

      // API returns paginated response with results array
      if (response && response.results && response.results.length > 0) {
        const activeModule = response.results[0];
        commit('setModule', activeModule);
        commit('setModuleId', activeModule.module_name);
        commit('setModuleName', activeModule.module_name);
        return activeModule;
      }

      // No active subscription found - set to default module
      commit('setModule', {});
      commit('setModuleId', 'default');
      commit('setModuleName', 'Default Module');
      return null;
    } catch (err) {
      console.error('Error fetching module:', err);
      throw err;
    }
  },

  async updateModule({ rootGetters, commit }, payload) {
    const accountId = rootGetters.getCurrentAccountId;

    const body = {
      module_name: payload.module_name,
    };

    try {
      const { data: response } = await axios.post(
        knowledgeProxy(accountId, `/module-subscription/${accountId}/modules`),
        body
      );

      commit('setModule', response);
      commit('setModuleId', response.module_name);
      commit('setModuleName', response.module_name);

      return response;
    } catch (err) {
      console.error('Error updating module:', err);
      throw err;
    }
  },

  async unsubscribeModule({ rootGetters, commit }, moduleName) {
    const accountId = rootGetters.getCurrentAccountId;

    try {
      await axios.delete(
        knowledgeProxy(accountId, `/module-subscription/${accountId}/modules/${moduleName}`)
      );
      // Reflect backend reality: no active module → default.
      commit('setModule', {});
      commit('setModuleId', 'default');
      commit('setModuleName', 'Default Module');
      return true;
    } catch (err) {
      console.error('Error unsubscribing from module:', err);
      throw err;
    }
  },

  async fetchAvailableModules({ rootGetters }) {
    const accountId = rootGetters.getCurrentAccountId;

    try {
      const { data: response } = await axios.get(
        knowledgeProxy(accountId, '/module-subscription/modules/available')
      );
      return response;
    } catch (err) {
      console.error('Error fetching available modules:', err);
      throw err;
    }
  },

  async fetchCreditThresholds({ rootGetters, commit }) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('setCreditThresholdsUiFlag', { isFetching: true });
    try {
      const { data: response } = await axios.get(
        knowledgeProxy(accountId, `/credit-notifications/${accountId}/thresholds`)
      );
      commit('setCreditThresholds', response);
    } catch (err) {
      console.error('Error fetching credit notification thresholds:', err);
      useAlert('Error fetching credit notification thresholds, please try again later.');
      throw err;
    } finally {
      commit('setCreditThresholdsUiFlag', { isFetching: false });
    }
  },

  async updateCreditThresholds({ rootGetters, commit }, thresholds) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('setCreditThresholdsUiFlag', { isUpdating: true });
    try {
      const { data: response } = await axios.put(
        knowledgeProxy(accountId, `/credit-notifications/${accountId}/thresholds`),
        { thresholds }
      );
      commit('setCreditThresholds', response);
      return response;
    } catch (err) {
      console.error('Error updating credit notification thresholds:', err);
      throw err;
    } finally {
      commit('setCreditThresholdsUiFlag', { isUpdating: false });
    }
  },

  async fetchNotificationEmails({ rootGetters, commit }) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('setNotificationEmailsUiFlag', { isFetching: true });
    try {
      const { data: response } = await axios.get(
        knowledgeProxy(accountId, `/credit-notifications/${accountId}/emails`)
      );
      commit('setNotificationEmails', response.emails);
    } catch (err) {
      console.error('Error fetching credit notification emails:', err);
      useAlert('Error fetching notification emails, please try again later.');
      throw err;
    } finally {
      commit('setNotificationEmailsUiFlag', { isFetching: false });
    }
  },

  async updateNotificationEmails({ rootGetters, commit }, emails) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('setNotificationEmailsUiFlag', { isUpdating: true });
    try {
      const { data: response } = await axios.put(
        knowledgeProxy(accountId, `/credit-notifications/${accountId}/emails`),
        { emails }
      );
      commit('setNotificationEmails', response.emails);
      return response;
    } catch (err) {
      console.error('Error updating credit notification emails:', err);
      throw err;
    } finally {
      commit('setNotificationEmailsUiFlag', { isUpdating: false });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
